import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTableProps } from './types';
import { getBackgroundColor } from './utils';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

// Tooltip map for metric column headers. Keys must match the raw column
// strings from the backend df_eval exactly (case-sensitive: "delta [min]",
// "sMAPE"); the header text is upper-cased via CSS only.
const METRIC_TOOLTIPS: Record<string, string> = {
  'delta [min]': 'tooltips.metrics.delta',
  MAE: 'tooltips.metrics.mae',
  MAPE: 'tooltips.metrics.mape',
  MSE: 'tooltips.metrics.mse',
  RMSE: 'tooltips.metrics.rmse',
  NRMSE: 'tooltips.metrics.nrmse',
  WAPE: 'tooltips.metrics.wape',
  sMAPE: 'tooltips.metrics.smape',
  MASE: 'tooltips.metrics.mase',
};

// LaTeX formulas per metric (rendered via KaTeX in the tooltip, same format as
// the source template). Math is language-independent, so these stay in-code
// rather than in the i18n locale. ŷ = predicted value, y = true value, n = count.
const METRIC_FORMULAS: Record<string, string> = {
  MAE: '\\text{MAE}=\\dfrac{1}{n}\\sum_{t=1}^{n}\\lvert \\hat{y}-y\\rvert',
  MAPE: '\\text{MAPE}=\\dfrac{1}{n}\\sum_{t=1}^{n}\\dfrac{\\lvert \\hat{y}-y\\rvert}{y}\\cdot 100\\%',
  MSE: '\\text{MSE}=\\dfrac{1}{n}\\sum_{t=1}^{n}\\left(\\hat{y}-y\\right)^2',
  RMSE: '\\text{RMSE}=\\sqrt{\\dfrac{1}{n}\\sum_{t=1}^{n}\\left(\\hat{y}-y\\right)^2}',
  NRMSE: '\\text{NRMSE}=\\dfrac{\\text{RMSE}}{y_{max}-y_{min}}',
  WAPE: '\\text{WAPE}=\\dfrac{\\sum_{t=1}^{n}\\lvert \\hat{y}-y\\rvert}{\\sum_{t=1}^{n}\\lvert y\\rvert}',
  sMAPE: '\\text{sMAPE}=\\dfrac{1}{n}\\sum_{t=1}^{n}\\dfrac{\\lvert \\hat{y}-y\\rvert}{\\left(y+\\hat{y}\\right)/2}\\cdot 100\\%',
  MASE: '\\text{MASE}=\\dfrac{\\text{MAE}}{\\text{MAE}_{naive}}',
};

/**
 * DataTable component for rendering evaluation metrics table
 * Memoized to prevent unnecessary re-renders
 */
export const DataTable: React.FC<DataTableProps> = memo(({ tableData }) => {
  const { t } = useTranslation('training');

  // Collect all numeric values for each column for color mapping
  const columnValues = useMemo(() => {
    const values: Record<string, number[]> = {};
    tableData.columns.forEach(column => {
      values[column] = (tableData.data as Record<string, unknown>[])
        .map(row => row[column])
        .filter(val => typeof val === 'number' && !isNaN(val)) as number[];
    });
    return values;
  }, [tableData.columns, tableData.data]);

  if (!tableData || !tableData.data || tableData.data.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-8">
        <p className="text-sm">{t('training.evaluation.noData')}</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card overflow-visible shadow-sm">
      <div className="px-4 py-2.5 sm:px-5 sm:py-3 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border">
        <h3 className="text-xs sm:text-sm font-semibold text-foreground tracking-tight">{tableData.title}</h3>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-muted/80">
              {tableData.columns.map((column, index) => (
                <th
                  key={index}
                  className="px-2 py-1.5 sm:px-3 sm:py-2 text-center text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b border-border"
                  style={{ minWidth: '60px' }}
                >
                  {METRIC_TOOLTIPS[column] ? (
                    <div className="inline-flex items-center justify-center gap-0.5">
                      <span>{column}</span>
                      <InfoTooltip text={t(METRIC_TOOLTIPS[column])} formula={METRIC_FORMULAS[column]} width="w-96" align={index >= tableData.columns.length - 3 ? 'end' : 'start'} />
                    </div>
                  ) : (
                    column
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.data.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-b border-border last:border-b-0">
                {tableData.columns.map((column, colIndex) => {
                  const value = row[column];
                  const bgColor = getBackgroundColor(value, column, columnValues[column] || []);
                  const textColor = bgColor.includes('700') || bgColor.includes('800') || bgColor.includes('900')
                    ? 'text-white'
                    : 'text-foreground';

                  return (
                    <td
                      key={colIndex}
                      className={`px-2 py-1 sm:px-3 sm:py-1.5 text-center text-[11px] sm:text-xs font-medium tabular-nums ${bgColor} ${textColor}`}
                      style={{ minWidth: '60px' }}
                    >
                      {typeof value === 'number'
                        ? (column === 'delta [min]'
                          ? value.toFixed(1)
                          : value.toFixed(5)
                        )
                        : String(value || 'N/A')
                      }
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

DataTable.displayName = 'DataTable';

export default DataTable;
